import React, { useMemo, useState } from 'react'
import { AssistantHint } from 'src/components/assistant-hint'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { BanknotesIcon, PlusIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { Spinner } from 'src/components/spinner'
import { Modal } from 'src/components/modal'
import { InlineHelp } from 'src/components/inline-help'
import { useToast } from 'src/components/toast'
import { usePermissions } from 'src/hooks/use-permissions'
import { cn } from 'src/utils/style'
import { billingApi } from 'src/services/billing/api'
import { billingQueries } from 'src/services/billing/queries'
import {
  UsageEventTypeValue,
  UsageRecord,
  usageEventTypeLabelMap,
} from 'src/services/billing/models'
import { InfoHint } from 'src/components/info-hint'

const formatRate = (amount: string | number, currency = 'USD') => {
  const value = typeof amount === 'string' ? Number(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(Number.isFinite(value) ? value : 0)
}

const formatCurrency = (amount: string | number, currency = 'USD') => {
  const value = typeof amount === 'string' ? Number(amount) : amount
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    Number.isFinite(value) ? value : 0,
  )
}

type PeriodKey = 'this_month' | 'last_month' | 'custom'

const asApiDate = (date: Date) => format(date, 'yyyy-MM-dd')

/** Roll up per-period/event rows into one total per display label, combining
 * LLM input + output tokens under a single "LLM tokens" line. */
const summariseByEventType = (records: UsageRecord[]) => {
  const totals = new Map<string, { label: string; units: number; cost: number }>()
  records.forEach((record) => {
    const label =
      usageEventTypeLabelMap[record.eventType as UsageEventTypeValue] ?? record.eventType
    const existing = totals.get(label) ?? { label, units: 0, cost: 0 }
    existing.units += record.totalUnits
    existing.cost += Number(record.totalCost)
    totals.set(label, existing)
  })
  return Array.from(totals.values()).sort((a, b) => b.cost - a.cost)
}

interface BillingSectionProps {
  /** Organization whose billing is shown — driven by the settings org switcher. */
  orgId: string
}

/**
 * Billing section of the organization settings page.
 *
 * Renders the wallet balance for any member with `billing.access` (the parent
 * gates rendering on that). Usage, pricing, and top-up are only shown to holders
 * of `billing.manage` in this specific organization.
 */
export const BillingSection: React.FC<BillingSectionProps> = ({ orgId }) => {
  const { t } = useTranslation()
  const { orgsWithPermission } = usePermissions()
  const canManage = orgsWithPermission('billing.manage').includes(orgId)

  const [period, setPeriod] = useState<PeriodKey>('this_month')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [isTopUpOpen, setIsTopUpOpen] = useState(false)

  const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
    { key: 'this_month', label: t('billing.periodThisMonth') },
    { key: 'last_month', label: t('billing.periodLastMonth') },
    { key: 'custom', label: t('billing.periodCustom') },
  ]

  const range = useMemo(() => {
    const now = new Date()
    if (period === 'this_month') {
      return { startDate: asApiDate(startOfMonth(now)), endDate: asApiDate(endOfMonth(now)) }
    }
    if (period === 'last_month') {
      const lastMonth = subMonths(now, 1)
      return {
        startDate: asApiDate(startOfMonth(lastMonth)),
        endDate: asApiDate(endOfMonth(lastMonth)),
      }
    }
    return {
      startDate: customStart || undefined,
      endDate: customEnd || undefined,
    }
  }, [period, customStart, customEnd])

  const { data: wallet, isLoading: walletLoading } = useQuery(billingQueries.wallet(orgId))

  // Pricing and usage are manage-only reads; don't fetch them for readonly members.
  const { data: rates, isLoading: ratesLoading } = useQuery({
    ...billingQueries.rates(orgId),
    enabled: canManage && Boolean(orgId),
  })

  const { data: usage, isLoading: usageLoading } = useQuery({
    ...billingQueries.usage({
      organization: orgId,
      startDate: range.startDate,
      endDate: range.endDate,
      groupBy: 'day',
    }),
    enabled: canManage && Boolean(orgId),
  })

  const breakdown = useMemo(() => summariseByEventType(usage ?? []), [usage])
  const currency = wallet?.currency ?? 'USD'

  return (
    <div id="billing" className="space-y-6" data-testid="billing-section">
      {/* Balance card */}
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        data-testid="wallet-balance-card"
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">
            {t('billing.sectionTitle')}
            <InfoHint text={t('billing.sectionSubtitle')} className="ml-1.5" />
          </h3>
          <InlineHelp
            className="mt-2"
            text={t('help.walletBalance')}
            detail={t('help.walletBalanceDetail')}
            data-testid="help-wallet-balance"
          />
        </div>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <BanknotesIcon className="h-5 w-5 text-primary" aria-hidden="true" />
              {t('billing.currentBalance')}
            </div>
            {walletLoading ? (
              <div className="mt-2">
                <Spinner size="sm" />
              </div>
            ) : (
              <div
                className="mt-1 text-3xl font-bold text-text-primary"
                data-testid="wallet-balance"
              >
                {formatCurrency(wallet?.balance ?? '0', currency)}
              </div>
            )}
          </div>
          {canManage && (
            <Button
              variant="primary"
              onClick={() => setIsTopUpOpen(true)}
              icon={<PlusIcon className="h-4 w-4" />}
              data-testid="topup-button"
            >
              {t('billing.topUpButton')}
            </Button>
          )}
        </div>
      </div>

      {canManage && (
        <>
          {/* Pricing / rate card */}
          <div
            className="rounded-lg border border-border bg-surface p-6 shadow-sm"
            data-testid="pricing-section"
          >
            <div className="mb-4">
              <h3 className="text-lg font-medium text-text-primary">
                {t('billing.pricing')}
                <InfoHint text={t('billing.pricingHint')} className="ml-1.5" />
              </h3>
            </div>
            {ratesLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="pricing-loading">
                <Spinner size="md" />
              </div>
            ) : !rates?.length ? (
              <div
                className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
                data-testid="pricing-empty"
              >
                {t('billing.pricingUnavailable')}
              </div>
            ) : (
              <div
                className="overflow-x-auto rounded-md border border-border"
                data-testid="pricing-table"
              >
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border bg-surface-hover text-xs uppercase text-text-secondary">
                    <tr>
                      <th className="px-4 py-3 font-medium">{t('billing.feature')}</th>
                      <th className="px-4 py-3 font-medium">{t('billing.unit')}</th>
                      <th className="px-4 py-3 text-right font-medium">
                        {t('billing.costPerUnit')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-surface">
                    {rates.map((rate) => (
                      <tr key={rate.eventType} data-testid={`pricing-row-${rate.eventType}`}>
                        <td className="px-4 py-3 text-text-primary">{rate.eventLabel}</td>
                        <td className="px-4 py-3 text-text-secondary">{rate.unitLabel}</td>
                        <td className="px-4 py-3 text-right text-text-secondary">
                          {formatRate(rate.costPerUnit, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Usage breakdown */}
          <div
            className="rounded-lg border border-border bg-surface p-6 shadow-sm"
            data-testid="usage-breakdown"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-medium text-text-primary">
                  {t('billing.usage')}
                  <InfoHint text={t('billing.usageHint')} className="ml-1.5" />
                </h3>
              </div>
              <div
                className="flex gap-1 rounded-lg border border-border p-1"
                data-testid="period-selector"
              >
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setPeriod(option.key)}
                    className={cn([
                      'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                      period === option.key
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:bg-surface-hover',
                    ])}
                    data-testid={`period-${option.key}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <AssistantHint id="billing-usage" text={t('assistantHint.usage')} className="mb-4" />

            {period === 'custom' && (
              <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="custom-range">
                <Input
                  type="date"
                  label={t('billing.startDate')}
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  data-testid="custom-start"
                />
                <Input
                  type="date"
                  label={t('billing.endDate')}
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  data-testid="custom-end"
                />
              </div>
            )}

            {usageLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="usage-loading">
                <Spinner size="md" />
              </div>
            ) : breakdown.length === 0 ? (
              <div
                className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
                data-testid="usage-empty"
              >
                {t('billing.noUsage')}
              </div>
            ) : (
              <div
                className="overflow-x-auto rounded-md border border-border"
                data-testid="usage-table"
              >
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border bg-surface-hover text-xs uppercase text-text-secondary">
                    <tr>
                      <th className="px-4 py-3 font-medium">{t('billing.eventType')}</th>
                      <th className="px-4 py-3 text-right font-medium">{t('billing.units')}</th>
                      <th className="px-4 py-3 text-right font-medium">{t('billing.spend')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-surface">
                    {breakdown.map((row) => (
                      <tr key={row.label} data-testid={`usage-row-${row.label}`}>
                        <td className="px-4 py-3 text-text-primary">{row.label}</td>
                        <td className="px-4 py-3 text-right text-text-secondary">
                          {row.units.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary">
                          {formatCurrency(row.cost, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent top-ups */}
          <div
            className="rounded-lg border border-border bg-surface p-6 shadow-sm"
            data-testid="recent-topups"
          >
            <h3 className="mb-4 text-lg font-medium text-text-primary">
              {t('billing.recentTopups')}
            </h3>
            {walletLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : !wallet?.recentTopups?.length ? (
              <div
                className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
                data-testid="topups-empty"
              >
                {t('billing.noTopups')}
              </div>
            ) : (
              <ul className="divide-y divide-border" data-testid="topups-list">
                {wallet.recentTopups.map((topup) => (
                  <li
                    key={topup.stripeSessionId}
                    className="flex items-center justify-between py-3"
                    data-testid={`topup-${topup.stripeSessionId}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {formatCurrency(topup.amount, currency)}
                      </div>
                      <div className="text-xs text-text-placeholder">
                        {format(new Date(topup.date), 'PP')}
                      </div>
                    </div>
                    <span className="text-xs capitalize text-text-secondary">{topup.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <TopUpModal
            isOpen={isTopUpOpen}
            onClose={() => setIsTopUpOpen(false)}
            organization={orgId}
          />
        </>
      )}
    </div>
  )
}

interface TopUpModalProps {
  isOpen: boolean
  onClose: () => void
  organization: string | null
}

const MINIMUM_TOPUP = 5

const TopUpModal = ({ isOpen, onClose, organization }: TopUpModalProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [amount, setAmount] = useState('25')

  const topUp = useMutation({
    mutationFn: () => billingApi.csc.createTopUp({ organization: organization!, amount }),
    onSuccess: (data) => {
      // Hand off to Stripe's hosted Checkout — the wallet is credited by webhook
      // on return.
      window.location.href = data.checkoutUrl
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || t('billing.topUpFailed')
      toast({ title: t('common.error'), description: message, type: 'error' })
    },
  })

  const numericAmount = Number(amount)
  const isValid = Number.isFinite(numericAmount) && numericAmount >= MINIMUM_TOPUP

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('billing.topUp')} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">
          {t('billing.topUpModalHint', { min: MINIMUM_TOPUP })}
        </p>
        <Input
          type="number"
          label={t('billing.amountUsd')}
          min={MINIMUM_TOPUP}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="topup-amount"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={topUp.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => topUp.mutate()}
            disabled={!isValid || !organization}
            isLoading={topUp.isPending}
            data-testid="topup-submit"
          >
            {t('billing.continueToPayment')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
